import React, { useState, useEffect } from 'react';
import { UI_VERSION, UI_GIT_SHA, ApiVersionInfo } from '../config/version';
import { fetchApiVersion } from '../services/version-service';
import { cn } from '../utils';

interface VersionDisplayProps {
  collapsed?: boolean;
  className?: string;
}

export function VersionDisplay({ collapsed = false, className }: VersionDisplayProps) {
  const [apiVersion, setApiVersion] = useState<ApiVersionInfo | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchApiVersion().then(setApiVersion);
  }, []);

  const uiVersionShort = UI_VERSION;
  const apiVersionShort = apiVersion?.version || '...';

  if (collapsed) {
    return (
      <div 
        className={cn(
          'px-2 py-2 text-xs text-gray-500 dark:text-secondary-400 text-center cursor-pointer hover:text-gray-700 dark:hover:text-secondary-200',
          className
        )}
        title={`UI: ${UI_VERSION}${UI_GIT_SHA !== 'local' ? `+${UI_GIT_SHA}` : ''}\nAPI: ${apiVersion?.fullVersion || 'N/A'}`}
      >
        <i className="fa-solid fa-code-branch"></i>
      </div>
    );
  }

  return (
    <div className={cn('px-4 py-3 border-t border-gray-200 dark:border-secondary-700', className)}>
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="w-full text-left text-xs text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 transition-colors"
      >
        <div className="flex items-center justify-between">
          <span className="font-medium">Version Info</span>
          <i className={cn(
            'fa-solid text-[10px] transition-transform',
            showDetails ? 'fa-chevron-up' : 'fa-chevron-down'
          )}></i>
        </div>
        
        {!showDetails && (
          <div className="mt-1 flex justify-between text-gray-400 dark:text-secondary-500">
            <span>UI: {uiVersionShort}</span>
            <span>API: {apiVersionShort}</span>
          </div>
        )}
      </button>

      {showDetails && (
        <div className="mt-2 space-y-2 text-xs">
          <div className="bg-gray-50 dark:bg-secondary-900 rounded p-2">
            <div className="font-medium text-gray-600 dark:text-secondary-400 mb-1">UI</div>
            <div className="text-gray-500 dark:text-secondary-400">
              <div>Version: {UI_VERSION}</div>
              {UI_GIT_SHA !== 'local' && <div>Commit: {UI_GIT_SHA}</div>}
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-secondary-900 rounded p-2">
            <div className="font-medium text-gray-600 dark:text-secondary-400 mb-1">API</div>
            {apiVersion ? (
              <div className="text-gray-500 dark:text-secondary-400">
                <div>Version: {apiVersion.version}</div>
                {apiVersion.gitCommit && <div>Commit: {apiVersion.gitCommit}</div>}
              </div>
            ) : (
              <div className="text-gray-400 dark:text-secondary-500">Loading...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
